import { useNavigate } from 'react-router-dom'
import { ArrowUpRight, CalendarPlus, Sparkles } from 'lucide-react'
import { cn } from '@lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import type { Appointment } from '@/types'
import { doctorLabel, patientLabel } from '@hooks/useScarlettInbox'

interface HeroTodayProps {
  greeting: string
  doctorName: string
  dateLabel: string
  appointmentsToday: number
  remainingToday: number
  pendingApprovals: number
  newNotes: number
  next: Appointment | null
  isLoading: boolean
}

const ACTIVE = new Set(['scheduled', 'confirmed', 'checked_in', 'in_progress'])

function minutesUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 60_000)
}

function countdown(a: Appointment): string {
  const m = minutesUntil(a.appointment_date)
  if (m <= 0 && m > -(a.duration_minutes || 30)) return 'en curso'
  if (m <= 0) return 'ya pasó'
  if (m < 60) return `en ${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r ? `en ${h} h ${r} min` : `en ${h} h`
}

export function HeroToday({ greeting, doctorName, dateLabel, appointmentsToday, remainingToday, pendingApprovals, newNotes, next, isLoading }: HeroTodayProps) {
  const navigate = useNavigate()
  const { formatTime } = useClinicFormat()

  const summary = (() => {
    if (isLoading) return 'Preparando tu día…'
    const parts: string[] = []
    parts.push(appointmentsToday === 0
      ? 'Hoy no tienes citas agendadas'
      : `Hoy tienes ${appointmentsToday} ${appointmentsToday === 1 ? 'cita' : 'citas'}${remainingToday > 0 && remainingToday < appointmentsToday ? `, ${remainingToday} por atender` : ''}`)
    parts.push(pendingApprovals > 0
      ? `${pendingApprovals} ${pendingApprovals === 1 ? 'solicitud por WhatsApp espera' : 'solicitudes por WhatsApp esperan'} tu confirmación`
      : 'sin solicitudes pendientes')
    if (newNotes > 0) parts.push(`${newNotes} ${newNotes === 1 ? 'nota nueva' : 'notas nuevas'} de recepción`)
    return parts.join(' · ') + '.'
  })()

  return (
    <section className="relative overflow-hidden rounded-3xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest shadow-sm">
      {/* Ornamento: dos halos en los tonos de la casa */}
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-ml-primary-fixed/50 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-[rgb(var(--ml-accent-soft)/0.45)] blur-3xl" />

      <div className="relative grid grid-cols-1 gap-8 p-6 sm:p-8 lg:grid-cols-12 lg:gap-10 lg:p-10">
        <div className="min-w-0 lg:col-span-7 flex flex-col gap-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-ml-on-surface-variant/60">{dateLabel}</p>
          <h1 className="font-headline text-4xl leading-[1.05] text-ml-primary sm:text-5xl lg:text-6xl text-balance">
            {greeting}, <span className="italic">{doctorName}</span>
          </h1>
          <p className="max-w-xl text-base leading-relaxed text-ml-on-surface-variant sm:text-lg">{summary}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              onClick={() => navigate('/agenda')}
              className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-ml-primary px-5 text-sm font-semibold text-ml-on-primary shadow-sm transition hover:bg-ml-primary-container"
            >
              <CalendarPlus className="h-4 w-4" /> Abrir agenda
            </button>
            <button
              onClick={() => navigate('/patients')}
              className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-ml-outline-variant/30 bg-ml-surface-container-lowest px-5 text-sm font-semibold text-ml-primary transition hover:bg-ml-surface-container-low"
            >
              Pacientes <ArrowUpRight className="h-4 w-4" />
            </button>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ml-on-surface-variant/70 sm:ml-2">
              <Sparkles className="h-3.5 w-3.5 text-ml-primary" /> Recepción por WhatsApp activa con Scarlett
            </span>
          </div>
        </div>

        {/* Ticket: siguiente cita */}
        <div className="min-w-0 lg:col-span-5">
          <div className={cn(
            'relative h-full rounded-2xl border p-5 sm:p-6 flex flex-col justify-between gap-4',
            next ? 'border-ml-primary/20 bg-ml-primary text-ml-on-primary' : 'border-ml-outline-variant/20 bg-ml-surface-container-low text-ml-on-surface',
          )}>
            {isLoading ? (
              <div className="h-32 animate-pulse rounded-xl bg-white/10" />
            ) : next ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-ml-on-primary/70">
                    {ACTIVE.has(next.status) && minutesUntil(next.appointment_date) <= 0 ? 'En consulta' : 'Siguiente cita'}
                  </p>
                  <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold tabular-nums">{countdown(next)}</span>
                </div>
                <div>
                  <p className="font-headline text-5xl leading-none tabular-nums sm:text-6xl">{formatTime(next.appointment_date)}</p>
                  <p className="mt-3 text-lg font-semibold leading-tight">{patientLabel(next)}</p>
                  <p className="text-sm text-ml-on-primary/75">
                    {next.reason || next.appointment_type}
                    {next.duration_minutes ? ` · ${next.duration_minutes} min` : ''}
                    {doctorLabel(next) ? ` · ${doctorLabel(next)}` : ''}
                  </p>
                </div>
                <button onClick={() => navigate('/agenda')} className="self-start text-sm font-semibold text-ml-on-primary/90 underline-offset-4 hover:underline">
                  Ver en la agenda →
                </button>
              </>
            ) : (
              <>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-ml-on-surface-variant/60">Siguiente cita</p>
                <div>
                  <p className="font-headline text-3xl text-ml-primary">Agenda libre por hoy</p>
                  <p className="mt-2 text-sm text-ml-on-surface-variant">Cuando un paciente agende por WhatsApp, aparecerá aquí al instante.</p>
                </div>
                <button onClick={() => navigate('/agenda')} className="self-start text-sm font-semibold text-ml-primary underline-offset-4 hover:underline">
                  Abrir agenda →
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
